import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Languages } from 'lucide-react';
import type { LanguageTabProps, LanguageSetting } from './types';

// Default labels matching current UI (British English)
const defaultLabels: Record<string, string> = {
  // General Labels
  subLocation: 'Sub Location',
  subCategory: 'Sub-Category',
  // Asset - User Keys
  userKey1: 'User Key 1',
  userKey2: 'User Key 2',
  userKey3: 'User Key 3',
  userKey4: 'User Key 4',
  userKey5: 'User Key 5',
  // Asset - User Values
  userValue1: 'Insurance Value (User Value 1)',
  userValue2: 'User Value 2',
  userValue3: 'User Value 3',
  userValue4: 'User Value 4',
  userValue5: 'Residual Costs (User Value 5)',
  // Asset - User Dates
  userDate1: 'User Date 1',
  userDate2: 'User Date 2',
  userDate3: 'User Date 3',
  // Equipment - User Text
  equipmentUserText1: 'User Text 1',
  equipmentUserText2: 'User Text 2',
  equipmentUserText3: 'User Text 3',
  equipmentUserText4: 'User Text 4',
  equipmentUserText5: 'User Text 5',
  equipmentUserText6: 'User Text 6',
  // Equipment - User Values
  equipmentUserValue1: 'User Value 1',
  equipmentUserValue2: 'User Value 2',
  equipmentUserValue3: 'User Value 3',
  // Equipment - User Dates
  equipmentUserDate1: 'User Date 1',
  equipmentUserDate2: 'User Date 2',
  equipmentUserDate3: 'User Date 3',
  // Equipment - User Options
  equipmentUserOption1: 'User Option 1',
  equipmentUserOption2: 'User Option 2',
  equipmentUserOption3: 'User Option 3',
};

interface FieldGroup {
  title: string;
  description: string;
  pageContext: string;
  fields: string[];
}

const fieldGroups: FieldGroup[] = [
  {
    title: 'General Labels',
    description: 'Customise labels for location and category hierarchies',
    pageContext: 'General',
    fields: ['subLocation', 'subCategory'],
  },
  {
    title: 'Asset - User Keys',
    description: 'Customise labels for user key fields on assets',
    pageContext: 'AssetCustomFields',
    fields: ['userKey1', 'userKey2', 'userKey3', 'userKey4', 'userKey5'],
  },
  {
    title: 'Asset - User Values',
    description: 'Customise labels for user value fields on assets',
    pageContext: 'AssetCustomFields',
    fields: ['userValue1', 'userValue2', 'userValue3', 'userValue4', 'userValue5'],
  },
  {
    title: 'Asset - User Dates',
    description: 'Customise labels for user date fields on assets',
    pageContext: 'AssetCustomFields',
    fields: ['userDate1', 'userDate2', 'userDate3'],
  },
  {
    title: 'Equipment - User Text',
    description: 'Customise labels for user text fields on equipment',
    pageContext: 'EquipmentCustomFields',
    fields: ['equipmentUserText1', 'equipmentUserText2', 'equipmentUserText3', 'equipmentUserText4', 'equipmentUserText5', 'equipmentUserText6'],
  },
  {
    title: 'Equipment - User Values',
    description: 'Customise labels for user value fields on equipment',
    pageContext: 'EquipmentCustomFields',
    fields: ['equipmentUserValue1', 'equipmentUserValue2', 'equipmentUserValue3'],
  },
  {
    title: 'Equipment - User Dates',
    description: 'Customise labels for user date fields on equipment',
    pageContext: 'EquipmentCustomFields',
    fields: ['equipmentUserDate1', 'equipmentUserDate2', 'equipmentUserDate3'],
  },
  {
    title: 'Equipment - User Options',
    description: 'Customise labels for user option fields on equipment',
    pageContext: 'EquipmentCustomFields',
    fields: ['equipmentUserOption1', 'equipmentUserOption2', 'equipmentUserOption3'],
  },
];

export function LanguageTab({ languageSettings, onLanguageSettingsChange, setHasUnsavedChanges }: Readonly<LanguageTabProps>): React.ReactElement {
  const handleLabelChange = (fieldKey: string, customLabel: string) => {
    const existingSetting = languageSettings.find((s) => s.fieldKey === fieldKey);
    
    if (existingSetting) {
      const updatedSettings = languageSettings.map((setting) => {
        if (setting.fieldKey === fieldKey) {
          return { ...setting, customLabel: customLabel || null };
        }
        return setting;
      });
      onLanguageSettingsChange(updatedSettings);
    } else {
      const fieldGroup = fieldGroups.find((group) => group.fields.includes(fieldKey));
      const newSetting: LanguageSetting = {
        id: crypto.randomUUID(),
        fieldKey,
        pageContext: fieldGroup?.pageContext ?? 'General',
        defaultLabel: defaultLabels[fieldKey] ?? fieldKey,
        customLabel: customLabel || null,
        createdDate: new Date().toISOString(),
      };
      onLanguageSettingsChange([...languageSettings, newSetting]);
    }
    setHasUnsavedChanges(true);
  };

  const getSettingByFieldKey = (fieldKey: string): LanguageSetting | undefined => {
    return languageSettings.find((s) => s.fieldKey === fieldKey);
  };

  const getDefaultLabel = (fieldKey: string): string => {
    return defaultLabels[fieldKey] ?? fieldKey;
  };

  const getCurrentValue = (fieldKey: string): string => {
    const setting = getSettingByFieldKey(fieldKey);
    // If there's a custom label, show it; otherwise show the default label as the value
    return setting?.customLabel ?? getDefaultLabel(fieldKey);
  };

  return (
    <div className="space-y-6">
      {/* Field Groups */}
      {fieldGroups.map((group) => (
        <Card key={group.title} className="border border-gray-200 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Languages className="h-5 w-5 text-primary" />
              {group.title}
            </CardTitle>
            <CardDescription>{group.description}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {group.fields.map((fieldKey) => {
                const defaultLabel = getDefaultLabel(fieldKey);
                const currentValue = getCurrentValue(fieldKey);
                
                return (
                  <div key={fieldKey} className="space-y-2">
                    <Label htmlFor={fieldKey} className="text-sm font-medium">
                      {defaultLabel}
                    </Label>
                    <Input
                      id={fieldKey}
                      type="text"
                      value={currentValue}
                      onChange={(e) => handleLabelChange(fieldKey, e.target.value)}
                      placeholder={defaultLabel}
                      className="text-sm"
                    />
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
